import React from 'react';
import { Box, Typography, Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import { ExpandMore } from '@mui/icons-material';
import SEOHead from '../components/SEO/SEOHead';

const FAQ_ITEMS = [
  { q: 'How do I create an account on Hooliah?', a: 'You can sign up using your Google account. Click the "Login" button in the header and authenticate with Google. Your profile will be created automatically.' },
  { q: 'How do I upload an outfit?', a: 'Once logged in, click the "Upload" button in the navigation bar. Select a photo of your outfit, add details like occasion, brands, and tags, then submit. Our AI will automatically analyze your outfit.' },
  { q: 'How does the rating system work?', a: 'Any logged-in user can rate outfits on a 1-5 star scale. Ratings are averaged across all users. Higher-rated outfits appear in the trending section and get more visibility.' },
  { q: 'What does the AI analysis do?', a: 'Our AI (powered by AWS Bedrock) analyzes your outfit photo to identify clothing items, colors, brands, and style patterns. This helps other users discover your look through search and recommendations.' },
  { q: 'Is my personal data safe?', a: 'Yes. We use industry-standard security practices including encrypted connections, secure authentication via Google OAuth, and AWS infrastructure. See our Privacy Policy for full details on how we handle your data.' },
  { q: 'How are ads displayed on Hooliah?', a: 'We use Google AdSense to display relevant fashion-related advertisements. Ads are placed in content feeds with a minimum gap of 8 items between ads and never within blog article content. You can manage ad personalization through Google\'s Ad Settings.' },
  { q: 'What are the community guidelines?', a: 'All content must be fashion-related and appropriate for a teen and young-adult audience. We do not allow abusive, sexually explicit, or off-topic content. Our automated moderation system and admin team review flagged content.' },
  { q: 'Can I write blog articles?', a: 'Yes! Logged-in users can write fashion articles through the blog editor. Articles must be at least 500 words and fashion-related. Admin users\' articles are published immediately; other users\' articles go through a review process.' },
  { q: 'How do I delete my account?', a: 'You can request account deletion by contacting us at support@hooliah.com. We will process your request and remove your personal data within 30 days.' },
  { q: 'Can I use Hooliah on my phone?', a: 'Absolutely! Hooliah is fully responsive and works on all devices — desktop, tablet, and mobile. You can also install it as a Progressive Web App (PWA) for a native app-like experience.' },
  { q: 'How do I report inappropriate content?', a: 'You can flag any comment using the flag button. For outfit or article concerns, contact our moderation team at support@hooliah.com. We review all reports promptly.' },
];

const FAQPage: React.FC = () => {
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQ_ITEMS.map((item) => ({
      '@type': 'Question',
      name: item.q,
      acceptedAnswer: { '@type': 'Answer', text: item.a },
    })),
  };

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto' }}>
      <SEOHead
        title="Frequently Asked Questions"
        description="Find answers to common questions about Hooliah — accounts, uploads, ratings, AI analysis, privacy, and more."
        canonicalUrl="https://hooliah.com/faq"
        jsonLd={jsonLd}
      />

      <Typography variant="h3" component="h1" sx={{ fontWeight: 700, mb: 1 }}>FAQ</Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Answers to the most common questions about Hooliah.
      </Typography>

      {FAQ_ITEMS.map((item, i) => (
        <Accordion key={i} disableGutters>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{item.q}</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="body1">{item.a}</Typography>
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>
  );
};

export default FAQPage;
